import { useEffect, useState } from "react";
import Select from "react-select";
import Datatable from "./Datatable";
import {
    handleApiData,
    getMonthlySales,
    getShopReport,
    initData,
} from "../../store/actions";
import { useData, useDispatch } from "../../store/DataProvider";

const NEW_SHOP = -1;

const Shops = () => {
    const dispatch = useDispatch();
    const { shopsObj, shopReport, monthlySales } = useData();

    /* the month list is the one the Sales screen builds, the way the Expenses
       screen takes it -- there is one answer to "which months have anything in
       them" and it should not be worked out twice */
    const { options: monthOptions, selected } = { ...monthlySales?.selectObj };
    const [selectedMonth, setSelectedMonth] = useState();

    const [selectedShopId, setSelectedShopId] = useState();
    /* kept apart from the selection, as on the expense types screen: the form
       closes on save -- there is no way to dismiss it otherwise -- but the row
       it belonged to stays marked so the place in the list is not lost */
    const [lastEditedId, setLastEditedId] = useState();
    const [name, setName] = useState();

    const formIsValid = !!(name && name.trim());
    const isNew = Number(selectedShopId) === NEW_SHOP;
    const selected_ = isNew ? null : shopsObj?.[selectedShopId];
    const useCount = selected_?.useCount || 0;

    useEffect(() => {
        if (!monthlySales?.selectObj) {
            getMonthlySales(null, dispatch);
        }
    }, [monthlySales, dispatch]);

    useEffect(() => {
        if (!shopsObj) {
            initData(dispatch);
        }
    }, [shopsObj, dispatch]);

    useEffect(() => {
        if (!selected?.value) {
            return;
        }
        setSelectedMonth(selected);
        getShopReport(selected.value, dispatch);
    }, [selected?.value]);

    const rowStyles = [
        {
            when: row =>
                Boolean(row.shopId) &&
                (row.shopId === selectedShopId ||
                    row.shopId === lastEditedId),
            style: {
                backgroundColor: "var(--row-selected)",
                fontWeight: "bold",
            },
        },
        /* the summary reads as a block under the shops rather than as more of
           them, the same way the Sales totals do */
        {
            when: row => Boolean(row.ruleAbove),
            style: { borderTop: "2px solid rgba(0, 0, 0, .26)" },
        },
        {
            when: row => Boolean(row.totalsRow),
            style: { fontWeight: "bold" },
        },
    ];

    const onSelectMonth = opt => {
        setSelectedMonth(opt);
        getShopReport(opt.value, dispatch);
    };

    const refreshReport = () => {
        if (selectedMonth?.value) {
            getShopReport(selectedMonth.value, dispatch);
        }
    };

    /* Only the shop rows open the editor. "Not attributed" and "Totals" are
       not shops and carry no id, so a click on them does nothing. */
    const onClickRow = row => {
        if (!row.shopId) {
            return;
        }
        setSelectedShopId(row.shopId);
        setLastEditedId(null);
        setName(shopsObj?.[row.shopId]?.name);
    };

    const onClickAdd = () => {
        setSelectedShopId(NEW_SHOP);
        setLastEditedId(null);
        setName("");
    };

    const resetFormFields = () => {
        setSelectedShopId(null);
        setName(null);
    };

    const onClickSubmit = async () => {
        /* safety measure - shouldn't be possible */
        if (!formIsValid) {
            window.alert("Please give the shop a name.");
            return;
        }
        const editedId = isNew ? null : selectedShopId;
        resetFormFields();
        /* the handler returns the rebuilt shop data with its reply, so the
           editor's own figures need no fetching; the report does, because it
           is built for a month and the handler knows nothing of which one.
           Awaited so the refresh cannot race the write. */
        await handleApiData(
            { shopId: selectedShopId, name },
            dispatch,
            "saveShop",
        );
        setLastEditedId(editedId);
        refreshReport();
    };

    const onClickDelete = async () => {
        if (
            !window.confirm(
                `Delete ${selected_.name}?\n\nNothing is filed under it. This cannot be undone.`,
            )
        ) {
            return;
        }
        const id = selectedShopId;
        setLastEditedId(null);
        resetFormFields();
        await handleApiData({ shopId: id }, dispatch, "deleteShop");
        refreshReport();
    };

    /* Lifetime counts, not this month's. It sits directly beneath a table of
       one month's figures, so it says so: the shop showing 52 sales in July
       has 1,951 altogether, and without the word both numbers look like they
       are answering the same question. Delete depends on the lifetime one --
       on whether anything at all still points at the shop. */
    const usageNote = () => {
        const { sales = 0, expenses = 0 } = selected_ || {};
        const parts = [];
        if (sales) {
            parts.push(`${sales} sale${sales === 1 ? "" : "s"}`);
        }
        if (expenses) {
            parts.push(`${expenses} expense${expenses === 1 ? "" : "s"}`);
        }
        return `${parts.join(" and ")} in total are filed under this shop`;
    };

    return (
        <div className="admin-wrapper">
            <div className="admin-column">
                <div className="data-wrapper expense-types-pane shops-pane">
                    <div className="expense-types-title">Shops</div>

                    <div className="select-wrapper" style={{ borderRight: 0 }}>
                        <Select
                            value={selectedMonth}
                            onChange={onSelectMonth}
                            options={monthOptions}
                        />
                    </div>

                    <div className="expense-types-list">
                        <Datatable
                            settings={shopReport}
                            loading="Loading ..."
                            clickFunc={onClickRow}
                            conditionalRowStyles={rowStyles}
                        />
                    </div>

                    {selectedShopId ? (
                        <div className="admin-staff-container">
                            <div className="admin-staff-row">
                                <div className="admin-input-container">
                                    <div className="admin-staff-label">
                                        Shop name:
                                    </div>
                                    <input
                                        className="admin-staff-input"
                                        type="text"
                                        placeholder="Namjing 1"
                                        value={name ? name : ""}
                                        onChange={e => setName(e.target.value)}
                                    />
                                </div>
                            </div>

                            <div
                                className="admin-staff-row"
                                style={{ marginTop: "5px" }}
                            >
                                <div className="admin-input-container">
                                    <button
                                        type="button"
                                        className="admin-staff-button"
                                        disabled={!formIsValid}
                                        onClick={onClickSubmit}
                                        style={{
                                            opacity: formIsValid ? 1 : ".4",
                                        }}
                                    >
                                        Save Changes
                                    </button>
                                </div>
                            </div>

                            <div className="expenses-edit-actions">
                                <button
                                    type="button"
                                    className="expenses-cancel-button"
                                    onClick={resetFormFields}
                                >
                                    Cancel
                                </button>
                                {isNew ? null : (
                                    <button
                                        type="button"
                                        className="admin-orphan-button"
                                        onClick={onClickDelete}
                                        disabled={useCount > 0}
                                        style={{
                                            opacity: useCount > 0 ? 0.4 : 1,
                                            cursor:
                                                useCount > 0
                                                    ? "default"
                                                    : "pointer",
                                        }}
                                    >
                                        Delete
                                    </button>
                                )}
                            </div>

                            {isNew || !useCount ? null : (
                                <div className="admin-type-note">
                                    {usageNote()}
                                </div>
                            )}
                        </div>
                    ) : (
                        /* only when the editor is closed, so there is one
                           thing to do at a time rather than an invitation to
                           start a second shop halfway through naming one */
                        <div className="admin-orphan-row">
                            <button
                                type="button"
                                className="admin-dedupe-button"
                                onClick={onClickAdd}
                            >
                                Add shop
                            </button>
                        </div>
                    )}
                </div>
                <div className="admin-gutter-right" />
            </div>
        </div>
    );
};

export default Shops;
